import React, { useState } from 'react';
import type { SlaClockKey, SlaEscalationLevel, SlaEscalationRow } from '@troia-v3/types';

import { Button, Input, MultiSelect, Select, Switch, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';

import {
  applyOffsetFieldMinutesChange, applyOffsetFieldSenseChange,
  CLOCK_KEY_LABELS, ESCALATION_LEVEL_LABELS, findDuplicateEscalationIndex,
  offsetMsToMinutes, offsetSenseOf,
  type OffsetFieldState, type OffsetSense, type SlaPolicyDraft,
} from '../utils/slaPolicyHelpers';

export interface SlaEscalationTabProps {
  draft: SlaPolicyDraft;
  onChange: (next: SlaPolicyDraft) => void;
  enabled: boolean;
}

const CLOCK_OPTIONS = (Object.keys(CLOCK_KEY_LABELS) as SlaClockKey[])
  .map((value) => ({ value, label: CLOCK_KEY_LABELS[value] }));

const LEVEL_OPTIONS = (Object.keys(ESCALATION_LEVEL_LABELS) as SlaEscalationLevel[])
  .map((value) => ({ value, label: ESCALATION_LEVEL_LABELS[value] }));

const SENSE_OPTIONS: Array<{ value: OffsetSense; label: string }> = [
  { value: 'before', label: 'antes do vencimento' },
  { value: 'at', label: 'no vencimento' },
  { value: 'after', label: 'depois do vencimento' },
];

interface SlaEscalationOffsetFieldProps {
  /** `offsetMs` do rascunho PAI — usado SÓ para inicializar o estado local
   *  (mount) e para o "reverter ao último válido" do blur. NUNCA lido de novo
   *  pra recomputar `sense`/minutos a cada render — mesmo motivo do
   *  `SlaTargetDurationField` (C18), ver JSDoc de `OffsetFieldState`. */
  offsetMs: number;
  /** Sobe pro rascunho pai só quando o campo tem um estado committável
   *  (minutos válido em before/after, ou sense === 'at'). */
  onCommit: (offsetMs: number) => void;
  minutesTestId: string;
  senseTestId: string;
}

/**
 * Campo "Disparar [minutos] min [sentido]" com estado PRÓPRIO
 * (`OffsetFieldState`) — `sense` é escolha do usuário, NUNCA recomputada de
 * `offsetMs` a cada render. Réplica do mecanismo do `SlaTargetDurationField`
 * (C18): a versão que deriva `sense`/minutos direto de `row.offsetMs` a cada
 * render reproduz a mesma classe de bug das rodadas 1/2 do campo de prazo —
 * apagar o campo pra redigitar passa por `offsetMs === 0`, e
 * `offsetSenseOf(0)` sempre retorna `'at'`, o que troca o Select de sentido
 * sozinho E desabilita o próprio campo de minutos (a UI desabilita o campo
 * quando `sense === 'at'`) — o usuário fica travado no meio da digitação.
 */
const SlaEscalationOffsetField: React.FC<SlaEscalationOffsetFieldProps> = ({
  offsetMs, onCommit, minutesTestId, senseTestId,
}) => {
  const [state, setState] = useState<OffsetFieldState>(() => ({
    rawMinutes: String(offsetMsToMinutes(offsetMs)),
    sense: offsetSenseOf(offsetMs),
  }));

  const handleMinutesChange = (raw: string) => {
    const { next, commitMs } = applyOffsetFieldMinutesChange(state, raw);
    setState(next);
    if (commitMs !== null) onCommit(commitMs);
  };

  const handleSenseChange = (sense: OffsetSense) => {
    const { next, commitMs } = applyOffsetFieldSenseChange(state, sense);
    setState(next);
    if (commitMs !== null) onCommit(commitMs);
  };

  const handleBlur = () => {
    if (state.sense === 'at') return; // campo desabilitado nesse sentido — nada a reverter
    const numeric = Number(state.rawMinutes);
    if (state.rawMinutes !== '' && Number.isFinite(numeric) && numeric > 0 && Number.isInteger(numeric)) return; // já válido
    // Campo abandonado num estado inválido (vazio, "0", fracionário) — volta
    // a mostrar o último valor de fato salvo no rascunho pai.
    setState((prev) => ({ ...prev, rawMinutes: String(offsetMsToMinutes(offsetMs)) }));
  };

  return (
    <>
      <Input
        type="number"
        min={1}
        value={state.rawMinutes}
        disabled={state.sense === 'at'}
        onChange={(e) => handleMinutesChange(e.target.value)}
        onBlur={handleBlur}
        fullWidth={false}
        style={{ width: 84, textAlign: 'center' }}
        data-testid={minutesTestId}
      />
      <Select
        value={state.sense}
        onChange={(value) => handleSenseChange(value as OffsetSense)}
        options={SENSE_OPTIONS}
        fullWidth={false}
        data-testid={senseTestId}
      />
    </>
  );
};

SlaEscalationOffsetField.displayName = 'SlaEscalationOffsetField';

/**
 * Aba "Escalação" — quem é avisado, e quando, em relação ao vencimento.
 *
 * `offsetMs` é ASSINADO: negativo = aviso antecipado, zero = no vencimento,
 * positivo = depois. O formulário separa SENTIDO de MAGNITUDE de propósito: um
 * campo único de "minutos" faria o usuário digitar `-5` para avisar antes, e
 * quem esquecesse o sinal agendaria o aviso 5 minutos DEPOIS da violação — o
 * oposto exato do que queria, sem nenhum erro na tela.
 *
 * O par (relógio, nível) é único: dois avisos do mesmo nível no mesmo relógio
 * disparariam a mesma notificação duas vezes, e o servidor rejeita com 422.
 */
export const SlaEscalationTab: React.FC<SlaEscalationTabProps> = ({ draft, onChange, enabled }) => {
  const { data: teams } = useTeamsDropdown({ enabled });
  const duplicateIndex = findDuplicateEscalationIndex(draft.escalation);

  const patchRow = (index: number, patch: Partial<SlaEscalationRow>) =>
    onChange({
      ...draft,
      escalation: draft.escalation.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });

  const addRow = () =>
    onChange({
      ...draft,
      escalation: [
        ...draft.escalation,
        {
          clockKey: 'first_response',
          offsetMs: -5 * 60_000,
          level: 'warning',
          notify: { assignee: true, managers: false, teamIds: [] },
        },
      ],
    });

  const removeRow = (index: number) =>
    onChange({ ...draft, escalation: draft.escalation.filter((_, i) => i !== index) });

  return (
    <div className="space-y-4" data-testid="sla-policy-escalation-tab">
      <p className="text-xs leading-relaxed" style={{ color: '#64748b' }}>
        Avisos disparados em relação ao vencimento de cada relógio. Sem nenhuma linha, a política
        só marca a violação — ninguém é notificado.
      </p>

      {draft.escalation.map((row, index) => (
        <div
          // Mesma regra da aba Alvos (`SlaTargetsTab`): a `key` não pode
          // incluir `clockKey`/`level`, senão trocar um desses Selects remonta
          // `SlaEscalationOffsetField` e o sentido/minutos volta a ser derivado
          // de `offsetMs` — o flip que o `OffsetFieldState` existe para
          // impedir. Identidade da linha = posição.
          key={`escalation-row-${index}`}
          className="p-4 rounded-xl"
          style={{
            backgroundColor: '#fafafa',
            border: `1px solid ${duplicateIndex === index ? '#dc2626' : '#e8e8ec'}`,
          }}
          data-testid={`sla-policy-escalation-row-${index}`}
        >
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Relógio"
              value={row.clockKey}
              onChange={(value) => patchRow(index, { clockKey: value as SlaClockKey })}
              options={CLOCK_OPTIONS}
              data-testid={`sla-policy-escalation-clock-${index}`}
            />
            <Select
              label="Nível"
              value={row.level}
              onChange={(value) => patchRow(index, { level: value as SlaEscalationLevel })}
              options={LEVEL_OPTIONS}
              data-testid={`sla-policy-escalation-level-${index}`}
            />
          </div>

          <div className="flex flex-wrap items-end gap-3 mt-4">
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              Disparar
              <SlaEscalationOffsetField
                offsetMs={row.offsetMs}
                onCommit={(offsetMs) => patchRow(index, { offsetMs })}
                minutesTestId={`sla-policy-escalation-minutes-${index}`}
                senseTestId={`sla-policy-escalation-sense-${index}`}
              />
              min
            </label>

            <span className="flex-1" />

            <Tooltip content="Remover esta linha">
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="p-1.5 rounded"
                style={{ color: '#ef4444' }}
                data-testid={`sla-policy-escalation-remove-${index}`}
              >
                <Icon name="trash" size={16} />
              </button>
            </Tooltip>
          </div>

          <div className="flex flex-wrap items-center gap-6 mt-4">
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              <Switch
                checked={row.notify.assignee}
                onChange={(checked) => patchRow(index, { notify: { ...row.notify, assignee: checked } })}
                data-testid={`sla-policy-escalation-notify-assignee-${index}`}
              />
              Responsável
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ color: '#334155' }}>
              <Switch
                checked={row.notify.managers}
                onChange={(checked) => patchRow(index, { notify: { ...row.notify, managers: checked } })}
                data-testid={`sla-policy-escalation-notify-managers-${index}`}
              />
              Gestores do contexto
            </label>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium mb-2" style={{ color: '#1e293b' }}>
              Equipes avisadas
            </label>
            <MultiSelect
              options={(teams ?? []).map((t) => ({ value: t.id, label: t.name }))}
              selectedValues={row.notify.teamIds}
              onChange={(values) => patchRow(index, { notify: { ...row.notify, teamIds: values } })}
              placeholder="Nenhuma equipe"
              data-testid={`sla-policy-escalation-teams-${index}`}
            />
          </div>

          {duplicateIndex === index && (
            <p className="text-xs mt-2" style={{ color: '#dc2626' }} data-testid={`sla-policy-escalation-error-${index}`}>
              Já existe uma linha de {ESCALATION_LEVEL_LABELS[row.level]} para{' '}
              {CLOCK_KEY_LABELS[row.clockKey]}.
            </p>
          )}
        </div>
      ))}

      <Button variant="secondary" icon="plus" onClick={addRow} data-testid="sla-policy-escalation-add">
        Adicionar linha de escalação
      </Button>
    </div>
  );
};

SlaEscalationTab.displayName = 'SlaEscalationTab';
